import type { ReactElement } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import styled from 'styled-components';

export interface SelectOption<T> {
  value: T;
  label: string;
}

interface Props<T> {
  options: SelectOption<T>[];
  value: T;
  onChange?: (value: T) => void;
  /** Toggle text shown when no option matches the current value. */
  placeholder?: string;
  /** Leading Font Awesome icon class on the toggle. */
  icon?: string;
  /** More compact variant. */
  small?: boolean;
  className?: string;
}

const Toggle = styled(Dropdown.Toggle)<{ $small: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: ${({ $small }): string => ($small ? '30px' : '34px')};
  background: rgba(17, 27, 51, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px;
  color: rgb(240, 242, 245) !important;
  font-weight: 500;
  font-size: ${({ $small }): string => ($small ? '12px' : '13px')};
  padding: ${({ $small }): string => ($small ? '4px 10px' : '6px 12px')} !important;
  white-space: nowrap;
  transition: all var(--transition);

  &::after {
    content: none;
  }

  &:hover,
  &:focus,
  &:active {
    background: rgba(17, 27, 51, 0.6) !important;
    box-shadow: none !important;
  }

  .caret {
    margin-left: 4px;
    font-size: 13px;
    color: var(--white60);
    transition: transform 0.2s;
  }

  &[aria-expanded='true'] .caret {
    transform: rotate(180deg);
  }
`;

const Menu = styled(Dropdown.Menu)`
  padding: 4px;
  border: none;
  border-radius: 8px;
  background: #707685;
  color: rgb(255, 255, 255);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Item = styled(Dropdown.Item)`
  border-radius: 6px;
  padding: 8px 12px;
  color: rgb(255, 255, 255);
  font-size: 13px;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.14);
    color: rgb(255, 255, 255);
  }

  &.active,
  &:active {
    background: #1967d2;
    color: rgb(255, 255, 255);
  }
`;

/*
 * Reusable dropdown/select styled on the cc-input base. Wraps react-bootstrap's
 * Dropdown so we keep its positioning, keyboard and click-outside behavior.
 */
export function Select<T>({
  options,
  value,
  onChange,
  placeholder,
  icon,
  small = false,
  className
}: Props<T>): ReactElement {
  const selected = options.find((option) => option.value === value);

  return (
    <Dropdown className={className}>
      <Toggle forwardedAs='button' className='x-pseudo' $small={small}>
        {icon !== undefined && <i className={icon} />}
        {selected?.label ?? placeholder}
        <i className='caret fas fa-angle-down' />
      </Toggle>

      <Menu>
        {options.map((option) => (
          <Item
            key={option.label}
            active={option.value === value}
            onClick={(): void => {
              onChange?.(option.value);
            }}
          >
            {option.label}
          </Item>
        ))}
      </Menu>
    </Dropdown>
  );
}
